/**
 * Format a material quantity with its unit, auto-scaling within a family
 * (g↔kg, m↔cm↔km, mL↔L). Suffix is always emitted so the user sees the unit.
 *
 * Examples:
 *   formatMaterialQuantity(800, 'g')      -> '800 g'
 *   formatMaterialQuantity(1500, 'g')     -> '1.5 kg'
 *   formatMaterialQuantity(55000, 'g')    -> '55 kg'
 *   formatMaterialQuantity(0.5, 'm')      -> '50 cm'
 *   formatMaterialQuantity(8000, 'm')     -> '8 km'
 *   formatMaterialQuantity(12, 'pcs')     -> '12 pcs'
 *   formatMaterialQuantity(3, 'pair')     -> '3 pair'
 *
 * Fractional values keep up to 2 decimals; values below 0.01 keep ~2 significant
 * digits (0.0013 -> '0.0013') so tiny BOM fractions never display as 0. Values
 * below 0.0001 are treated as zero.
 */
import { formatCount } from '@utils/currency';

export function formatMaterialQuantity(
    value: number | null | undefined,
    unit: string | null | undefined,
): string {
    if (value == null || !Number.isFinite(value)) return '--';
    const u = unit ?? 'pcs';

    // Mass family: store in g, display in g/kg
    if (u === 'g') {
        if (Math.abs(value) >= 1000) {
            return `${formatNumber(value / 1000)} kg`;
        }
        return `${formatNumber(value)} g`;
    }
    if (u === 'kg') {
        return `${formatNumber(value)} kg`;
    }

    // Length family: store and display in m. We deliberately do NOT auto-scale
    // up to km — procurement talks in meters (or bolts of ~100 m), so 8,750 m
    // reads more naturally than 8.75 km. Down-scale to cm for sub-metre values.
    if (u === 'm') {
        if (Math.abs(value) > 0 && Math.abs(value) < 1) {
            return `${formatNumber(value * 100)} cm`;
        }
        return `${formatNumber(value)} m`;
    }
    if (u === 'cm') {
        return `${formatNumber(value)} cm`;
    }

    // Volume family
    if (u === 'mL') {
        if (Math.abs(value) >= 1000) {
            return `${formatNumber(value / 1000)} L`;
        }
        return `${formatNumber(value)} mL`;
    }
    if (u === 'L') {
        return `${formatNumber(value)} L`;
    }

    // Area, discrete counts — never auto-scaled
    return `${formatNumber(value)} ${u}`;
}

/**
 * @param maxDecimals Cap on fractional digits (default 2). Pass 1 where a number is a
 *   derived figure rather than a recorded one — a shortfall is `needed - available`, and
 *   binary floating point turns 25 - 2.9 - 12 into 10.100000000000001. Trailing digits
 *   there are arithmetic residue, not precision the user entered.
 */
export function formatNumber(value: number, maxDecimals = 2): string {
    // Up to maxDecimals for fractional values; group separators for the integer part
    if (Number.isInteger(value)) {
        return formatCount(value);
    }
    const abs = Math.abs(value);
    // Sub-0.01 values round to '0' at 2 decimals, which misreads as "not consumed"
    // for BOM per-FG fractions (e.g. 0.0013 boxes per garment). Show ~2 significant
    // digits instead. Below 0.0001 clamp to '0': at that magnitude the value is
    // floating-point residue from stock arithmetic, not signal.
    if (abs < 0.01) {
        if (abs < 0.0001) return '0';
        const decimals = Math.ceil(-Math.log10(abs)) + 1;
        return value
            .toFixed(decimals)
            .replace(/0+$/, '')
            .replace(/\.$/, '');
    }
    const factor = 10 ** maxDecimals;
    const rounded = Math.round(value * factor) / factor;
    // Rounding can land on a whole number (10.04 at 1dp -> 10); take the integer path.
    if (Number.isInteger(rounded)) return formatCount(rounded);
    const [intPart, fracPart] = rounded.toString().split('.');
    const groupedInt = formatCount(intPart);
    return fracPart ? `${groupedInt}.${fracPart}` : groupedInt;
}
